import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import TaskForm from './TaskForm';
import Dropdown from './Dropdown';
import DateInput from './DateInput';
import { A, TYPE_COLORS, CAT_COLORS, inputStyle as iStyle, btnStyle as avgBtn, iconBtnStyle as ib, btnSave, btnCancel } from './theme';

const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const TASK_TYPES = ['exam','class','assignment','lab','meeting','deadline','reminder','event','other'];
const MEM_CATEGORIES = ['personal','contact','credentials','academic','preference','other'];
const RECURRENCE_OPTIONS = [{v:null,l:'None'},{v:'daily',l:'Daily'},{v:'weekly',l:'Weekly'},{v:'custom',l:'Custom'}];

function dhakaToday() { return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dhaka', year:'numeric', month:'2-digit', day:'2-digit' }).format(new Date()); }
function getDayName(ds) { return DAY_NAMES[new Date(ds+'T12:00:00').getDay()]; }
function dhakaNow() {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dhaka', year: 'numeric', month: '2-digit', day: '2-digit' });
  const parts = fmt.formatToParts(new Date());
  const y = +parts.find(p=>p.type==='year').value;
  const m = +parts.find(p=>p.type==='month').value - 1;
  const d = +parts.find(p=>p.type==='day').value;
  return new Date(y, m, d);
}
function localDateStr(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth()+1).padStart(2,'0');
  const d = String(dt.getDate()).padStart(2,'0');
  return `${y}-${m}-${d}`;
}
function getWeekRange(off) {
  const n = dhakaNow();
  const d = n.getDay(); const s = new Date(n); s.setDate(n.getDate()-d+off*7);
  const e = new Date(s); e.setDate(s.getDate()+6);
  return { from: localDateStr(s), to: localDateStr(e) };
}
function getMonthRange() {
  const n = dhakaNow();
  return { from: localDateStr(new Date(n.getFullYear(),n.getMonth(),1)), to: localDateStr(new Date(n.getFullYear(),n.getMonth()+1,0)) };
}

function TasksPanel({ api, refreshKey, onDataChange }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [activeView, setActiveView] = useState('today');
  const [deleting, setDeleting] = useState(null);
  const [activeRange, setActiveRange] = useState({ from: '', to: '' });
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSelected, setUploadSelected] = useState(new Set());
  const [uploadEditing, setUploadEditing] = useState(null);
  const [uploadFile, setUploadFile] = useState(null); // staged file before extraction
  const [uploadPrompt, setUploadPrompt] = useState('');
  const uploadFileRef = useRef(null); // persists file across re-extracts
  const fileInputRef = useRef(null);
  const VIEW_LABELS = { today: 'Today', thisWeek: 'This Week', nextWeek: 'Next Week', thisMonth: 'This Month', search: 'Search Results' };
  const viewLabel = VIEW_LABELS[activeView] || '';

  const fetchAbortRef = useRef(null);
  const fetchTasks = useCallback(async (params, label, view, range) => {
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setLoading(true); setActiveView(view);
    if (range) setActiveRange(range); else setActiveRange({ from: '', to: '' });
    try { const r = await fetch(`${api}/dashboard/tasks?${params}`, { signal: controller.signal }); const d = await r.json(); if (!controller.signal.aborted) setTasks(d.data||[]); }
    catch (e) { if (e.name !== 'AbortError') setTasks([]); }
    if (!controller.signal.aborted) setLoading(false);
  }, [api]);

  const loadToday = useCallback(() => { const t=dhakaToday(); fetchTasks(`dateFrom=${t}&dateTo=${t}`,'Today','today'); }, [fetchTasks]);
  const loadThisWeek = useCallback(() => { const {from,to}=getWeekRange(0); fetchTasks(`dateFrom=${from}&dateTo=${to}`,'This Week','thisWeek',{from,to}); }, [fetchTasks]);
  const loadNextWeek = useCallback(() => { const {from,to}=getWeekRange(1); fetchTasks(`dateFrom=${from}&dateTo=${to}`,'Next Week','nextWeek',{from,to}); }, [fetchTasks]);
  const loadThisMonth = useCallback(() => { const {from,to}=getMonthRange(); fetchTasks(`dateFrom=${from}&dateTo=${to}`,'This Month','thisMonth',{from,to}); }, [fetchTasks]);

  useEffect(() => { loadToday(); }, [loadToday]);
  // Cleanup abort controller on unmount
  useEffect(() => { return () => { if (fetchAbortRef.current) fetchAbortRef.current.abort(); }; }, []);
  // Re-fetch current view when external data changes (LLM, other panel)
  useEffect(() => {
    if (refreshKey > 0) {
      const loaders = { today: loadToday, thisWeek: loadThisWeek, nextWeek: loadNextWeek, thisMonth: loadThisMonth };
      (loaders[activeView] || loadToday)();
    }
  }, [refreshKey]); // eslint-disable-line

  const searchCustom = async () => {
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const p = new URLSearchParams();
    if (search) p.set('keyword',search); if (dateFilter) p.set('date',dateFilter);
    if (dateFrom) p.set('dateFrom',dateFrom); if (dateTo) p.set('dateTo',dateTo);
    if (statusFilter!=='active') p.set('status',statusFilter);
    setLoading(true); setActiveView('search');
    try { const r = await fetch(`${api}/dashboard/tasks?${p}`, { signal: controller.signal }); const d = await r.json(); if (!controller.signal.aborted) setTasks(d.data||[]); }
    catch (e) { if (e.name !== 'AbortError') setTasks([]); }
    if (!controller.signal.aborted) setLoading(false);
  };

  const onDataChangeRef = useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  const deleteTask = useCallback(async id => { try { const r = await fetch(`${api}/dashboard/tasks/${id}`,{method:'DELETE'}); if(r.ok) { setTasks(p=>p.filter(t=>t.id!==id)); if(onDataChangeRef.current) onDataChangeRef.current(); } } catch{} setDeleting(null); }, [api]);
  const toggleComplete = useCallback(async task => { try { const r = await fetch(`${api}/dashboard/tasks/${task.id}/complete`,{method:'PATCH'}); if(r.ok){ const d = await r.json(); setTasks(p=>p.map(t=>t.id===task.id?{...t,completedAt:d.data?.completedAt??null}:t)); if(onDataChangeRef.current) onDataChangeRef.current(); } } catch{} }, [api]);
  const handleEdit = useCallback(t => { setEditing(t); setShowForm(true); }, []);
  const activeViewRef = useRef(activeView);
  activeViewRef.current = activeView;
  const searchCustomRef = useRef(searchCustom);
  searchCustomRef.current = searchCustom;
  const onSaved = useCallback(() => { setShowForm(false); setEditing(null); ({today:loadToday,thisWeek:loadThisWeek,nextWeek:loadNextWeek,thisMonth:loadThisMonth}[activeViewRef.current]||searchCustomRef.current)(); if(onDataChangeRef.current) onDataChangeRef.current(); }, [loadToday, loadThisWeek, loadNextWeek, loadThisMonth]);

  // ── Upload Handlers (unified: tasks + memories) ──
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    uploadFileRef.current = file;
    setUploadFile(file);
    setUploadPrompt('');
    setUploadError('');
    setUploadPreview(null);
  }, []);

  const handleExtract = useCallback(async () => {
    const file = uploadFileRef.current;
    if (!file) return;
    setUploadLoading(true);
    setUploadError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (uploadPrompt.trim()) fd.append('prompt', uploadPrompt.trim());
      const r = await fetch(`${api}/dashboard/upload/extract`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Extraction failed');
      setUploadPreview(d.data);
      setUploadSelected(new Set(d.data.map((_, i) => i)));
      setUploadFile(null); // hide prompt dialog, show preview
    } catch (err) {
      setUploadError(err.message);
    }
    setUploadLoading(false);
  }, [api, uploadPrompt]);

  const handleReExtract = useCallback(() => {
    // Go back to prompt dialog with same file
    setUploadFile(uploadFileRef.current);
    setUploadPreview(null);
    setUploadEditing(null);
    setUploadError('');
  }, []);

  const handleUploadConfirm = useCallback(async () => {
    if (!uploadPreview) return;
    const selected = uploadPreview.filter((_, i) => uploadSelected.has(i));
    if (selected.length === 0) return;
    setUploadLoading(true);
    try {
      const r = await fetch(`${api}/dashboard/upload/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: selected }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Creation failed');
      setUploadPreview(null);
      setUploadSelected(new Set());
      setUploadEditing(null);
      uploadFileRef.current = null;
      setUploadPrompt('');
      loadToday();
      if (onDataChangeRef.current) onDataChangeRef.current();
    } catch (err) {
      setUploadError(err.message);
    }
    setUploadLoading(false);
  }, [api, uploadPreview, uploadSelected, loadToday]);

  const toggleUploadItem = useCallback((idx) => {
    setUploadSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }, []);

  const updateUploadItem = useCallback((idx, field, value) => {
    setUploadPreview(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  }, []);

  const switchUploadKind = useCallback((idx) => {
    setUploadPreview(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      if (item.kind === 'task') {
        return { kind: 'memory', category: 'academic', key: item.title || '', value: item.description || '', group: null };
      }
      return { kind: 'task', title: item.key || '', type: 'other', date: null, time: null, recurrence: null, recurrenceDays: null, interval: null, description: item.value || '', until: null };
    }));
  }, []);

  const toggleRecurrenceDay = useCallback((idx, dayNum) => {
    setUploadPreview(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const days = Array.isArray(item.recurrenceDays) ? [...item.recurrenceDays] : [];
      const pos = days.indexOf(dayNum);
      if (pos >= 0) days.splice(pos, 1); else days.push(dayNum);
      days.sort();
      return { ...item, recurrenceDays: days.length ? days : null };
    }));
  }, []);

  // Build grouped view — for weekly views, include ALL 7 days even if empty
  const { grouped, sortedDates } = useMemo(() => {
    const g = {};
    tasks.forEach(t => { const d = t.date||'No date'; if(!g[d]) g[d]=[]; g[d].push(t); });
    // Fill in ALL days in the active range so every day shows as a block
    if (activeRange.from && activeRange.to) {
      const start = new Date(activeRange.from+'T12:00:00');
      const end = new Date(activeRange.to+'T12:00:00');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
        const ds = d.toISOString().slice(0,10);
        if (!g[ds]) g[ds] = [];
      }
    }
    return { grouped: g, sortedDates: Object.keys(g).sort() };
  }, [tasks, activeRange]);
  const isMultiDay = ['thisWeek','nextWeek','thisMonth'].includes(activeView);
  // Only recalculate today string when tasks change (covers midnight rollover on refresh)
  const today = useMemo(() => dhakaToday(), [refreshKey]); // eslint-disable-line

  return (
    <div>
      {/* Row 1: Quick views — flex:1 so they share space equally, never wrap */}
      <div style={{ display:'flex', gap:'3px', marginBottom:'8px' }}>
        {[{k:'today',l:'D',fl:'Today',fn:loadToday},{k:'thisWeek',l:'W',fl:'This Week',fn:loadThisWeek},{k:'nextWeek',l:'NW',fl:'Next Week',fn:loadNextWeek},{k:'thisMonth',l:'M',fl:'This Month',fn:loadThisMonth}].map(v=>(
          <button key={v.k} onClick={v.fn} title={v.fl} style={{
            flex:1, padding:'7px 0', borderRadius:'6px', border:'none', cursor:'pointer',
            background: activeView===v.k ? `linear-gradient(135deg, ${A.primary}25, ${A.accent}10)` : 'rgba(255,255,255,0.04)',
            color: activeView===v.k ? A.primary : A.dim,
            fontSize:'11px', fontWeight:600, letterSpacing:'0.5px', textTransform:'uppercase',
            transition:'background 0.2s, color 0.2s, border-color 0.2s', textAlign:'center',
            borderBottom: activeView===v.k ? `2px solid ${A.primary}` : '2px solid transparent',
          }}><span className="task-view-full">{v.fl}</span><span className="task-view-short">{v.l}</span></button>
        ))}
      </div>

      {/* Row 2: Search + Date filter + Status + Go */}
      <div style={{display:'flex',gap:'4px',marginBottom:'6px',alignItems:'stretch'}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search..."
          aria-label="Search tasks" onKeyDown={e=>e.key==='Enter'&&searchCustom()} style={{...iStyle,flex:1,minWidth:0}} />
        <DateInput value={dateFilter} onChange={e=>{setDateFilter(e.target.value);setDateFrom('');setDateTo('');}} placeholder="Date" aria-label="Filter by specific date" style={{...iStyle,flex:'0 0 auto',minWidth:0,padding:'7px 8px'}} />
        <Dropdown value={statusFilter} onChange={setStatusFilter} style={{width:'75px',flexShrink:0}} aria-label="Filter by status"
          options={[{value:'active',label:'Active'},{value:'completed',label:'Done'},{value:'all',label:'All'}]} />
        <button onClick={searchCustom} style={{...avgBtn,padding:'7px 10px',flexShrink:0}}>Go</button>
      </div>

      {/* Row 3: Date range */}
      <div style={{display:'flex',gap:'4px',marginBottom:'10px',alignItems:'stretch'}}>
        <DateInput value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setDateFilter('');}} placeholder="From" aria-label="Filter from date" style={{...iStyle,flex:1,minWidth:0,padding:'7px 8px'}} />
        <span aria-hidden="true" style={{color:A.dim,fontSize:'10px',flexShrink:0}}>→</span>
        <DateInput value={dateTo} onChange={e=>{setDateTo(e.target.value);setDateFilter('');}} placeholder="To" aria-label="Filter to date" style={{...iStyle,flex:1,minWidth:0,padding:'7px 8px'}} />
      </div>

      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
        <div>
          <h2 style={{margin:0,fontSize:'14px',color:A.text,fontWeight:600,letterSpacing:'1px',textTransform:'uppercase'}}>
            {viewLabel}
          </h2>
          <p style={{margin:'2px 0 0',fontSize:'10px',color:A.dim}}>{tasks.length} mission{tasks.length!==1?'s':''}</p>
        </div>
        <div style={{display:'flex',gap:'4px'}}>
          <button onClick={()=>fileInputRef.current?.click()} disabled={uploadLoading}
            title="Upload image to extract tasks or memories"
            aria-label="Upload image"
            style={{...avgBtn,background:`linear-gradient(135deg, ${A.accent}15, ${A.primary}10)`,border:`1px solid ${A.accent}25`,fontSize:'11px',padding:'8px 10px'}}>
            {uploadLoading ? '...' : '📤 Upload'}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*,.pdf,.txt,.csv,.md,.json,.doc,.docx" onChange={handleFileSelect} style={{display:'none'}} />
          <button onClick={()=>{setEditing(null);setShowForm(true)}} style={{...avgBtn,background:`linear-gradient(135deg, ${A.primary}25, ${A.accent}10)`,border:`1px solid ${A.primary}30`}}>+ New Mission</button>
        </div>
      </div>

      {/* Task List */}
      {loading ? (
        <div style={{textAlign:'center',padding:'40px',color:A.dim}}>
          <div style={{display:'inline-flex',gap:'4px'}}>
            {[0,1,2].map(i=><div key={i} style={{width:'6px',height:'6px',borderRadius:'50%',background:A.primary,animation:`td 1.4s ease-in-out ${i*0.2}s infinite`}}/>)}
          </div>
          <p style={{marginTop:'8px',fontSize:'11px',letterSpacing:'1px'}}>LOADING MISSIONS...</p>
        </div>
      ) : tasks.length === 0 ? (
        <p style={{color:A.dim,textAlign:'center',padding:'40px',fontSize:'12px',letterSpacing:'1px'}}>
          {activeView==='today'?'NO MISSIONS TODAY. STAND DOWN.':'NO MISSIONS FOUND.'}
        </p>
      ) : isMultiDay ? (
        sortedDates.map(date=>{
          const dayTasks = grouped[date];
          const isToday = date === today;
          const isEmpty = !dayTasks.length;
          return (
          <div key={date} style={{marginBottom:'16px',animation:'fsu 0.3s ease-out'}}>
            {/* Day block header */}
            <div style={{
              padding:'10px 14px',marginBottom:isEmpty?0:'8px',borderRadius:'8px',
              background: isToday ? `linear-gradient(135deg, ${A.primary}12, ${A.primary}05)` : 'rgba(255,255,255,0.02)',
              border: `1px solid ${isToday ? A.primary+'20' : 'rgba(255,255,255,0.04)'}`,
              borderLeft: isToday ? `3px solid ${A.primary}` : `3px solid rgba(255,255,255,0.06)`,
              display:'flex',justifyContent:'space-between',alignItems:'center',
            }}>
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <span style={{
                  fontSize:'14px',fontWeight:700,
                  color:isToday ? A.primary : isEmpty ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.6)',
                  letterSpacing:'0.5px',
                }}>
                  {getDayName(date)}
                </span>
                <span style={{fontSize:'11px',color:isToday?`${A.primary}80`:'rgba(255,255,255,0.2)'}}>
                  {date}
                </span>
                {isToday&&<span style={{fontSize:'9px',background:`${A.primary}25`,color:A.primary,padding:'2px 8px',borderRadius:'4px',fontWeight:700,letterSpacing:'1px'}}>TODAY</span>}
              </div>
              <span style={{
                fontSize:'11px',fontWeight:600,
                color:isEmpty ? 'rgba(255,255,255,0.15)' : isToday ? A.primary : A.dim,
              }}>
                {isEmpty ? 'Free' : `${dayTasks.length} task${dayTasks.length>1?'s':''}`}
              </span>
            </div>
            {/* Tasks under this day */}
            {!isEmpty && (
              <div style={{display:'flex',flexDirection:'column',gap:'5px',marginLeft:'8px',paddingLeft:'10px',borderLeft:`1px solid ${isToday?A.primary+'15':'rgba(255,255,255,0.03)'}`}}>
                {dayTasks.map(t=><TaskCard key={`${t.id}-${date}`} task={t} del={deleting} setDel={setDeleting} onDel={deleteTask} onComp={toggleComplete} onEdit={handleEdit} />)}
              </div>
            )}
          </div>
          );
        })
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
          {tasks.map(t=><TaskCard key={t.id} task={t} del={deleting} setDel={setDeleting} onDel={deleteTask} onComp={toggleComplete} onEdit={handleEdit} />)}
        </div>
      )}

      {/* Upload Prompt Dialog — appears after file selected */}
      {uploadFile && !uploadPreview && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'16px'}}
          onClick={(e)=>{if(e.target===e.currentTarget){setUploadFile(null);setUploadPrompt('');uploadFileRef.current=null;}}}
          role="dialog" aria-modal="true" aria-label="Upload instructions">
          <div style={{
            background:A.surfaceModal,borderRadius:'12px',border:`1px solid ${A.border}`,
            padding:'20px',maxWidth:'420px',width:'100%',
          }}>
            <h3 style={{margin:'0 0 8px',fontSize:'14px',color:A.text,letterSpacing:'1px'}}>UPLOAD IMAGE</h3>
            <p style={{margin:'0 0 4px',fontSize:'11px',color:A.dim}}>File: {uploadFile.name}</p>
            <p style={{margin:'0 0 12px',fontSize:'10px',color:A.dim}}>
              Add instructions to help AI understand the image (optional).
            </p>

            {uploadError && <p role="alert" style={{color:'#ef4444',fontSize:'11px',marginBottom:'8px'}}>{uploadError}</p>}

            <textarea value={uploadPrompt} onChange={e=>setUploadPrompt(e.target.value)}
              placeholder="e.g. This is my class routine, extract all classes as weekly tasks&#10;e.g. These are contact details, save as memories&#10;e.g. Extract assignment deadlines from this syllabus"
              rows={4}
              style={{...iStyle,width:'100%',boxSizing:'border-box',resize:'vertical',minHeight:'60px',fontSize:'12px',marginBottom:'12px'}}
              aria-label="Instructions for AI"
              autoFocus
              onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();handleExtract();}}}
            />

            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end'}}>
              <button onClick={()=>{setUploadFile(null);setUploadPrompt('');uploadFileRef.current=null;}} style={btnCancel}>CANCEL</button>
              <button onClick={handleExtract} disabled={uploadLoading}
                style={btnSave}>
                {uploadLoading ? 'EXTRACTING...' : 'EXTRACT'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Preview Modal — full editing for tasks + memories */}
      {uploadPreview && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'16px'}}
          onClick={(e)=>{if(e.target===e.currentTarget){setUploadPreview(null);setUploadError('');setUploadEditing(null);}}}
          role="dialog" aria-modal="true" aria-label="Upload preview">
          <div style={{
            background:A.surfaceModal,borderRadius:'12px',border:`1px solid ${A.border}`,
            padding:'20px',maxWidth:'520px',width:'100%',maxHeight:'85vh',overflowY:'auto',
          }}>
            <h3 style={{margin:'0 0 4px',fontSize:'14px',color:A.text,letterSpacing:'1px'}}>EXTRACTED DATA</h3>
            <p style={{margin:'0 0 12px',fontSize:'11px',color:A.dim}}>
              {uploadPreview.length} items found ({uploadPreview.filter(i=>i.kind==='task').length} tasks, {uploadPreview.filter(i=>i.kind==='memory').length} memories). Tap to edit, toggle kind, uncheck to skip.
            </p>

            {uploadError && <p role="alert" style={{color:'#ef4444',fontSize:'11px',marginBottom:'8px'}}>{uploadError}</p>}

            <div style={{display:'flex',flexDirection:'column',gap:'6px',marginBottom:'16px'}}>
              {uploadPreview.map((item, i) => {
                const isEd = uploadEditing === i;
                const ri = { ...iStyle, fontSize:'12px', padding:'6px 8px' };
                const isTask = item.kind === 'task';
                const kindColor = isTask ? A.primary : A.accent;

                return (
                <div key={i} style={{
                  padding:'10px 12px',borderRadius:'8px',
                  background: uploadSelected.has(i) ? `${kindColor}08` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isEd ? kindColor+'40' : uploadSelected.has(i) ? kindColor+'20' : 'rgba(255,255,255,0.04)'}`,
                  borderLeft: `3px solid ${kindColor}30`,
                  transition:'background 0.15s, border-color 0.15s',
                }}>
                  <div style={{display:'flex',alignItems:'flex-start',gap:'8px'}}>
                    <input type="checkbox" checked={uploadSelected.has(i)} onChange={()=>toggleUploadItem(i)}
                      style={{marginTop:'3px',accentColor:kindColor,flexShrink:0}} />

                    {isEd ? (
                      <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:'6px'}}>
                        {/* Kind toggle */}
                        <div style={{display:'flex',gap:'4px',alignItems:'center'}}>
                          <button onClick={()=>switchUploadKind(i)} style={{
                            ...ri,background:isTask?`${A.primary}15`:`${A.accent}15`,color:isTask?A.primary:A.accent,
                            border:`1px solid ${isTask?A.primary:A.accent}30`,cursor:'pointer',fontWeight:700,fontSize:'10px',letterSpacing:'0.5px',
                          }}>{isTask ? 'TASK' : 'MEMORY'} ↔</button>
                          <span style={{fontSize:'9px',color:A.dim}}>tap to switch</span>
                        </div>

                        {isTask ? (<>
                          {/* Task fields */}
                          <input value={item.title||''} onChange={e=>updateUploadItem(i,'title',e.target.value)}
                            placeholder="Title" style={ri} aria-label="Title" />
                          <div style={{display:'flex',gap:'4px',flexWrap:'wrap'}}>
                            <select value={item.type||'other'} onChange={e=>updateUploadItem(i,'type',e.target.value)}
                              style={{...ri,flex:1,minWidth:'70px'}} aria-label="Type">
                              {TASK_TYPES.map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                            </select>
                            <input value={item.time||''} onChange={e=>updateUploadItem(i,'time',e.target.value)}
                              placeholder="HH:MM" style={{...ri,flex:1,minWidth:'60px'}} aria-label="Time" />
                            <input value={item.date||''} onChange={e=>updateUploadItem(i,'date',e.target.value)}
                              placeholder="YYYY-MM-DD" style={{...ri,flex:1,minWidth:'90px'}} aria-label="Date" />
                          </div>
                          {/* Recurrence */}
                          <div style={{display:'flex',gap:'4px',flexWrap:'wrap',alignItems:'center'}}>
                            <select value={item.recurrence||''} onChange={e=>updateUploadItem(i,'recurrence',e.target.value||null)}
                              style={{...ri,flex:'0 0 auto'}} aria-label="Recurrence">
                              {RECURRENCE_OPTIONS.map(o=><option key={o.l} value={o.v||''}>{o.l}</option>)}
                            </select>
                            {item.recurrence && (
                              <input value={item.interval||1} onChange={e=>updateUploadItem(i,'interval',parseInt(e.target.value)||1)}
                                type="number" min="1" max="52" style={{...ri,width:'50px'}} aria-label="Interval" />
                            )}
                            {item.recurrence && <span style={{fontSize:'9px',color:A.dim}}>{item.recurrence==='daily'?'day(s)':'week(s)'}</span>}
                          </div>
                          {/* Day checkboxes for weekly */}
                          {(item.recurrence==='weekly'||item.recurrence==='custom') && (
                            <div style={{display:'flex',gap:'2px',flexWrap:'wrap'}}>
                              {DAY_NAMES.map((dn,di)=>{
                                const active = Array.isArray(item.recurrenceDays) && item.recurrenceDays.includes(di);
                                return <button key={di} onClick={()=>toggleRecurrenceDay(i,di)} style={{
                                  padding:'3px 6px',borderRadius:'4px',fontSize:'9px',fontWeight:600,cursor:'pointer',
                                  background:active?`${A.primary}20`:'rgba(255,255,255,0.04)',
                                  color:active?A.primary:A.dim,border:`1px solid ${active?A.primary+'30':'transparent'}`,
                                  minWidth:'32px',textAlign:'center',
                                }}>{dn.slice(0,3)}</button>;
                              })}
                            </div>
                          )}
                          {/* Until date */}
                          {item.recurrence && (
                            <input value={item.until||''} onChange={e=>updateUploadItem(i,'until',e.target.value||null)}
                              placeholder="Until (YYYY-MM-DD)" style={ri} aria-label="Until date" />
                          )}
                          <textarea value={item.description||''} onChange={e=>updateUploadItem(i,'description',e.target.value)}
                            placeholder="Description (room, teacher, notes...)" rows={2}
                            style={{...ri,resize:'vertical',minHeight:'36px'}} aria-label="Description" />
                        </>) : (<>
                          {/* Memory fields */}
                          <select value={item.category||'other'} onChange={e=>updateUploadItem(i,'category',e.target.value)}
                            style={ri} aria-label="Category">
                            {MEM_CATEGORIES.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                          </select>
                          <input value={item.key||''} onChange={e=>updateUploadItem(i,'key',e.target.value)}
                            placeholder="Key / Label" style={ri} aria-label="Key" />
                          <textarea value={item.value||''} onChange={e=>updateUploadItem(i,'value',e.target.value)}
                            placeholder="Value / Content" rows={3}
                            style={{...ri,resize:'vertical',minHeight:'50px'}} aria-label="Value" />
                          <input value={item.group||''} onChange={e=>updateUploadItem(i,'group',e.target.value||null)}
                            placeholder="Group (optional)" style={ri} aria-label="Group" />
                        </>)}

                        <button onClick={()=>setUploadEditing(null)}
                          style={{...btnSave,alignSelf:'flex-end',padding:'4px 12px',fontSize:'10px'}}>DONE</button>
                      </div>
                    ) : (
                      <div style={{flex:1,minWidth:0,cursor:'pointer'}} onClick={()=>setUploadEditing(i)}>
                        {isTask ? (<>
                          <div style={{display:'flex',alignItems:'center',gap:'6px',flexWrap:'wrap'}}>
                            <span style={{padding:'1px 5px',borderRadius:'3px',fontSize:'8px',fontWeight:700,background:`${A.primary}15`,color:A.primary,letterSpacing:'0.5px'}}>TASK</span>
                            <span style={{fontSize:'12px',fontWeight:600,color:A.text}}>{item.title}</span>
                            <span style={{
                              padding:'1px 6px',borderRadius:'4px',fontSize:'9px',fontWeight:700,
                              background:`${TYPE_COLORS[item.type]||'#6b7280'}18`,color:TYPE_COLORS[item.type]||'#6b7280',
                              letterSpacing:'0.5px',textTransform:'uppercase',
                            }}>{item.type}</span>
                          </div>
                          <div style={{fontSize:'11px',color:A.dim,marginTop:'2px',display:'flex',gap:'8px',flexWrap:'wrap'}}>
                            {item.time && <span>{item.time}</span>}
                            {item.date && <span>{item.date}</span>}
                            {item.recurrence && <span>🔁 {item.interval>1?`every ${item.interval} `:''}{item.recurrence}{item.recurrenceDays?.length?` (${item.recurrenceDays.map(d=>DAY_NAMES[d]?.slice(0,3)).join(', ')})`:''}</span>}
                            {item.until && <span>until {item.until}</span>}
                          </div>
                          {item.description && <p style={{margin:'2px 0 0',fontSize:'10px',color:A.dim,opacity:0.7}}>{item.description}</p>}
                        </>) : (<>
                          <div style={{display:'flex',alignItems:'center',gap:'6px',flexWrap:'wrap'}}>
                            <span style={{padding:'1px 5px',borderRadius:'3px',fontSize:'8px',fontWeight:700,background:`${A.accent}15`,color:A.accent,letterSpacing:'0.5px'}}>MEMORY</span>
                            <span style={{fontSize:'12px',fontWeight:600,color:A.text}}>{item.key}</span>
                            <span style={{
                              padding:'1px 6px',borderRadius:'4px',fontSize:'9px',fontWeight:700,
                              background:`${CAT_COLORS[item.category]||'#6b7280'}18`,color:CAT_COLORS[item.category]||'#6b7280',
                              letterSpacing:'0.5px',textTransform:'uppercase',
                            }}>{item.category}</span>
                          </div>
                          <p style={{margin:'2px 0 0',fontSize:'10px',color:A.dim,opacity:0.7}}>{item.value?.slice(0,100)}{item.value?.length>100?'...':''}</p>
                          {item.group && <span style={{fontSize:'9px',color:A.dim}}>group: {item.group}</span>}
                        </>)}
                        <span style={{fontSize:'9px',color:kindColor,opacity:0.6,marginTop:'2px',display:'inline-block'}}>tap to edit</span>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>

            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end'}}>
              <button onClick={()=>{setUploadPreview(null);setUploadError('');setUploadEditing(null);uploadFileRef.current=null;setUploadPrompt('');}} style={btnCancel}>CANCEL</button>
              <button onClick={handleReExtract}
                style={{...btnCancel,color:A.primary,borderColor:`${A.primary}30`}}>RE-EXTRACT</button>
              <button onClick={handleUploadConfirm} disabled={uploadLoading || uploadSelected.size===0}
                style={{...btnSave,opacity:uploadSelected.size===0?0.4:1}}>
                {uploadLoading ? 'CREATING...' : `CREATE ${uploadSelected.size} ITEMS`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && <TaskForm api={api} task={editing} onClose={()=>{setShowForm(false);setEditing(null)}} onSaved={onSaved} />}
    </div>
  );
}

const TaskCard = React.memo(function TaskCard({task:t, del, setDel, onDel, onComp, onEdit}) {
  return (
    <div style={{
      padding:'10px 14px',borderRadius:'8px',
      background:t.completedAt?'rgba(34,197,94,0.06)':A.surface,
      border:`1px solid ${t.completedAt?'rgba(34,197,94,0.12)':A.border}`,
      opacity:t.completedAt?0.6:1,
      animation:'fsu 0.2s cubic-bezier(.22,1,.36,1)',
      transition:'opacity 0.2s, border-color 0.2s',
    }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:'6px',flexWrap:'wrap',marginBottom:'2px'}}>
            <span style={{fontSize:'13px',fontWeight:600,color:A.text,textDecoration:t.completedAt?'line-through':'none'}}>{t.title}</span>
            <span style={{
              padding:'1px 6px',borderRadius:'4px',fontSize:'9px',fontWeight:700,
              background:`${TYPE_COLORS[t.type]||'#6b7280'}18`,color:TYPE_COLORS[t.type]||'#6b7280',
              letterSpacing:'0.5px',textTransform:'uppercase',
            }}>{t.type}</span>
            {t.completedAt&&<span style={{fontSize:'9px',color:'#22c55e',letterSpacing:'1px'}}>COMPLETE</span>}
          </div>
          <div style={{fontSize:'11px',color:A.dim,display:'flex',gap:'8px',flexWrap:'wrap'}}>
            {t.time&&<span>🕐 {t.time.slice(0,5)}</span>}
            {t.date&&!t.recurrence&&<span>📅 {t.date}</span>}
            {t.recurrence&&<span>🔁 {t.interval>1?`every ${t.interval} ${t.recurrence==='daily'?'days':'weeks'}`:t.recurrence}{t.recurrenceDays?.length ? ` (${t.recurrenceDays.map(d=>DAY_NAMES[d]?.slice(0,3)).join(', ')})` : ''}</span>}
            {t.until&&<span>⏳ until {t.until}</span>}
          </div>
          {t.description&&<p style={{margin:'4px 0 0',fontSize:'11px',color:'rgba(200,195,230,0.75)',lineHeight:'1.4',overflowWrap:'break-word'}}>{t.description}</p>}
        </div>
        <div style={{display:'flex',gap:'4px',flexShrink:0,marginLeft:'6px'}}>
          <button onClick={()=>onComp(t)} aria-label={t.completedAt?'Mark undone':'Mark done'} style={{...ib,color:t.completedAt?'#22c55e':A.dim}}>✓</button>
          <button onClick={()=>onEdit(t)} aria-label="Edit task" style={ib}>✎</button>
          {del===t.id?(
            <><button onClick={()=>onDel(t.id)} aria-label="Confirm delete" style={{...ib,color:'#ef4444'}}>✓</button><button onClick={()=>setDel(null)} aria-label="Cancel delete" style={ib}>✗</button></>
          ):(
            <button onClick={()=>setDel(t.id)} title="Delete" aria-label="Delete task" style={{...ib,color:'#ef4444'}}>✗</button>
          )}
        </div>
      </div>
    </div>
  );
});

/* Styles imported from theme.js */

export default TasksPanel;
